import { PersonType } from '@api';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export type PrivateInformationBirthdateType = {
  month: number | null;
  day: number | null;
  year: number | null;
};

export type PrivateInformationContactType = {
  phone: string | null;
  mobile: string | null;
  email: string | null;
};

export type PrivateInformationAddressType = {
  street: string | null;
  addition: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
};

export type PrivateInformationFormSchemaType = {
  firstname: string;
  lastname: string;
  nickname: string | null;
  birthdate: PrivateInformationBirthdateType;
  contact: PrivateInformationContactType;
  address: PrivateInformationAddressType;
  colorTourRepCalendar: string | null;
  userId: string | null;
};

const TRANS_KEY =
  'PRIVATE_INFORMATION.EDIT_PRIVATE_INFORMATION_FORM.FORM_SCHEMA';

const buildPrivateInformationFormSchema = (t: TFunction<'personDetailsGeneral'>) =>
  z.object({
    firstname: z.string().min(1, {
      message: t(`${TRANS_KEY}.FIRSTNAME_REQUIRED`),
    }),
    lastname: z.string().min(1, {
      message: t(`${TRANS_KEY}.LASTNAME_REQUIRED`),
    }),
    nickname: z.string().nullable(),
    birthdate: z.object({
      month: z.number().nullable(),
      day: z.number().nullable(),
      year: z.number().nullable(),
    }),
    contact: z.object({
      phone: z.string().nullable(),
      mobile: z.string().nullable(),
      email: z.string().nullable(),
    }),
    address: z.object({
      street: z.string().nullable(),
      addition: z.string().nullable(),
      zip: z.string().nullable(),
      city: z.string().nullable(),
      country: z.string().nullable(),
    }),
    colorTourRepCalendar: z.string().nullable(),
    userId: z.string().nullable(),
  });

type PrivateInformationFormSchema = ReturnType<
  typeof buildPrivateInformationFormSchema
>;

type UsePrivateInformationFormSchemaReturnType = {
  schema: PrivateInformationFormSchema;
  defaultValues: PrivateInformationFormSchemaType;
};

export const usePrivateInformationFormSchema = (
  personDetails: PersonType
): UsePrivateInformationFormSchemaReturnType => {
  const { t } = useTranslation('personDetailsGeneral');

  const schema = buildPrivateInformationFormSchema(t);

  const defaultValues: PrivateInformationFormSchemaType = personDetails;

  return { schema, defaultValues };
};
